import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { dayInRange, monthGrid, monthOf, quickPicks, shiftMonth } from '../lib/calendar';
import { addDays, formatDue, relativeDay } from '../lib/todos';
import { dateText, monthNames, weekdayNames } from '../i18n/dates';

/**
 * THE ONE DATE CONTROL (V0.51.1) — a button that opens a month grid.
 *
 * ── WHY THIS EXISTS AT ALL ───────────────────────────────────────────────────────────────
 *
 * `input[type=date]` is drawn by the browser, differently in each one, and it cannot carry the
 * thing people actually reach for: "tomorrow", "next Monday". Reaching tomorrow through a
 * native picker is three interactions for the single most common answer. It also cannot be
 * styled to sit inside a dark panel, and it fires `change` for every intermediate value while a
 * year is typed — `0002-09-19` is a valid day — which is why `ProjectHead` had to commit on
 * blur and grew a keyboard handler to go with it. None of that survives a control that can only
 * ever produce a real day.
 *
 * ONE implementation, at all five sites: the task editor, the project's start and target in the
 * popup head, and the Logs tab's two filters. The house rule, and the reason this is a
 * component rather than three.
 *
 * NOT A LIBRARY, because the app must run air-gapped (`connect-src 'self'`, bundle rather than
 * CDN): a picker dependency would have to be vendored whole and kept. The arithmetic is in
 * `lib/calendar.ts` and is thirty lines.
 *
 * ── THE TWO THINGS THAT WILL BREAK IF THEY ARE UNDONE ────────────────────────────────────
 *
 * 1. THE POPOVER IS PORTALLED TO `document.body` AND `position: fixed`. `ReslugButton`'s
 *    precedent: a `backdrop-filter` is a containing block for `position: fixed` exactly as a
 *    `transform` is, and `.docbar` has one — so a popover drawn from inside a bar is confined
 *    to it. The popup's own body scrolls and would clip it instead. Being placed by its callers
 *    is the whole point of the component, so it cannot depend on where it was placed.
 * 2. ESCAPE STOPS HERE. `app/chrome.ts` closes the node popup on Escape and the editor cancels
 *    on it; without `stopPropagation` the first press would throw away a draft somebody was
 *    only trying to close a calendar over.
 */

export interface DatePickerProps {
  /** The DOM id of the BUTTON — the specs address the control by it. */
  id?: string;
  value: string | null;
  onChange: (next: string | null) => void;
  /** Today, as `YYYY-MM-DD`. A parameter, never read from the clock here. */
  today: string;
  /** What the button says when there is no date. Defaults to "Set a date". */
  placeholder?: string;
  /** Draw the shortcut row. Off where a date is a filter bound rather than a plan. */
  quick?: boolean;
  min?: string | null;
  max?: string | null;
  disabled?: boolean;
  className?: string;
  locale?: string;
}

export function DatePicker({
  id,
  value,
  onChange,
  today,
  placeholder,
  quick = false,
  min = null,
  max = null,
  disabled = false,
  className,
  locale = 'en',
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState<string>(() => monthOf(value || today));
  const [box, setBox] = useState<{ left: number; top: number } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);

  /* Re-open on the month the value is in, not the one last paged to — somebody who opens a
     picker on a dated row expects to see that date, not wherever they wandered last time. */
  useEffect(() => {
    if (open) setMonth(monthOf(value || today));
  }, [open, value, today]);

  /**
   * Place it against the button, and keep it there.
   *
   * MEASURED, not guessed, and re-measured on scroll and resize: the popup's body scrolls under
   * a `fixed` popover, so a position computed once drifts away from its own button the moment
   * anybody scrolls. Flipped above when there is not room below, which is the ordinary case for
   * a row near the bottom of a long list.
   */
  const place = useCallback(() => {
    const el = btn.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const H = 340;
    const W = 258;
    const below = window.innerHeight - r.bottom;
    setBox({
      left: Math.max(8, Math.min(r.left, window.innerWidth - W - 8)),
      top: below < H && r.top > below ? Math.max(8, r.top - H - 6) : r.bottom + 6,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  /* Outside pointerdown closes. Capture, so it still fires when the thing underneath stops
     propagation for its own reasons. */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (pop.current?.contains(t) || btn.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [open]);

  const close = (focus = true) => {
    setOpen(false);
    if (focus) btn.current?.focus();
  };

  const pick = (day: string | null) => {
    onChange(day);
    close();
  };

  /** The button's own text: a relative word where there is one, else `19 Sep`. */
  const label = value
    ? ((): string => {
        const rel = relativeDay(value, today);
        return rel ? dateText(rel, locale) : formatDue(value, today);
      })()
    : (placeholder ?? dateText('setDate', locale));

  const months = monthNames(locale);
  const [gy, gm] = month.split('-').map(Number);
  const cells = monthGrid(month);

  /**
   * Arrows move a day at a time and a week at a time, PageUp/PageDown a month.
   *
   * Moving the SELECTION rather than focus: there are 42 buttons and a roving tabindex across
   * them is a lot of machinery for a grid whose whole job is "pick one". Enter on the grid
   * takes whatever is highlighted; Escape closes without choosing.
   */
  const onGridKey = (e: React.KeyboardEvent): boolean => {
    const step =
      e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : e.key === 'ArrowUp' ? -7 : e.key === 'ArrowDown' ? 7 : 0;
    if (step) {
      e.preventDefault();
      const next = addDays(value || today, step);
      if (dayInRange(next, min, max)) onChange(next);
      setMonth(monthOf(next));
      return true;
    }
    if (e.key === 'PageUp' || e.key === 'PageDown') {
      e.preventDefault();
      setMonth(shiftMonth(month, e.key === 'PageUp' ? -1 : 1));
      return true;
    }
    return false;
  };

  return (
    <>
      <button
        ref={btn}
        type="button"
        id={id}
        className={`due-btn${open ? ' on' : ''}${value ? ' set' : ''}${className ? ` ${className}` : ''}`}
        data-day={value ?? ''}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={dateText('openCalendar', locale)}
        onClick={() => setOpen((v) => !v)}
      >
        <svg className="ico" aria-hidden="true">
          <use href="#i-cal" />
        </svg>
        <span className="due-btn-text">{label}</span>
        {value && !disabled ? (
          <span
            className="clr"
            role="button"
            tabIndex={0}
            aria-label={dateText('clearDate', locale)}
            title={dateText('clearDate', locale)}
            /* The clear is INSIDE the button, so it has to stop the open from happening too. */
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' && e.key !== ' ') return;
              e.preventDefault();
              e.stopPropagation();
              onChange(null);
            }}
          >
            ×
          </span>
        ) : null}
      </button>

      {open && box
        ? createPortal(
            <div
              ref={pop}
              className="cal"
              data-cal
              data-month={month}
              role="dialog"
              aria-label={dateText('calendarLabel', locale)}
              style={{ left: box.left, top: box.top }}
              onKeyDown={(e) => {
                if (onGridKey(e)) {
                  e.stopPropagation();
                  return;
                }
                if (e.key === 'Escape') {
                  // Stops HERE: chrome.ts closes the popup on Escape and the editor cancels on
                  // it, and neither should happen because a calendar was open.
                  e.stopPropagation();
                  close();
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  if (value) pick(value);
                }
              }}
            >
              <div className="cal-head">
                <button
                  type="button"
                  className="cal-nav"
                  aria-label={dateText('previousMonth', locale)}
                  onClick={() => setMonth(shiftMonth(month, -1))}
                >
                  ‹
                </button>
                <b>
                  {months[gm - 1]} {gy}
                </b>
                <button
                  type="button"
                  className="cal-nav"
                  aria-label={dateText('nextMonth', locale)}
                  onClick={() => setMonth(shiftMonth(month, 1))}
                >
                  ›
                </button>
              </div>

              {quick ? (
                <div className="cal-quick">
                  {quickPicks(today)
                    .filter((p) => dayInRange(p.day, min, max))
                    .map((p) => (
                      <button key={p.key} type="button" data-pick={p.day} onClick={() => pick(p.day)}>
                        {dateText(p.key, locale)}
                      </button>
                    ))}
                  {value ? (
                    <button type="button" className="clear" data-pick="" onClick={() => pick(null)}>
                      {dateText('clear', locale)}
                    </button>
                  ) : null}
                </div>
              ) : null}

              <div className="cal-grid">
                {weekdayNames(locale).map((w, i) => (
                  <span className="wd" key={`${w}-${i}`}>
                    {w}
                  </span>
                ))}
                {cells.map((c) => {
                  const allowed = dayInRange(c.day, min, max);
                  return (
                    <button
                      key={c.day}
                      type="button"
                      className={[
                        'd',
                        c.outside ? 'out' : '',
                        c.day === today ? 'today' : '',
                        c.day === value ? 'sel' : '',
                        c.day < today ? 'past' : '',
                        c.weekday >= 5 ? 'we' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      data-day={c.day}
                      data-pick={c.day}
                      disabled={!allowed}
                      aria-current={c.day === today ? 'date' : undefined}
                      aria-pressed={c.day === value}
                      autoFocus={c.day === (value || today)}
                      onClick={() => pick(c.day)}
                    >
                      {Number(c.day.slice(8))}
                    </button>
                  );
                })}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
